import styled from 'styled-components';
import { ReactNode } from 'react';

export type StackDirection = 'horizontal' | 'vertical';
export type StackAlignment =
  | 'start'
  | 'center'
  | 'end'
  | 'stretch'
  | 'baseline';
export type StackJustify =
  | 'start'
  | 'center'
  | 'end'
  | 'between'
  | 'around'
  | 'evenly';
export type StackSpacing =
  | 'none'
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl';
export type StackWrap = 'nowrap' | 'wrap' | 'wrap-reverse';

const spacingMap: Record<StackSpacing, number> = {
  none: 0,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  '2xl': 32,
  '3xl': 48,
};

const alignmentMap: Record<StackAlignment, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
  baseline: 'baseline',
};

const justifyMap: Record<StackJustify, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
  evenly: 'space-evenly',
};

export interface StackProps {
  /** Children elements */
  children?: ReactNode;
  /** Stack direction */
  direction?: StackDirection;
  /** Alignment of items on cross axis */
  align?: StackAlignment;
  /** Justification of items on main axis */
  justify?: StackJustify;
  /** Gap between items (preset or number in pixels) */
  gap?: StackSpacing | number;
  /** Wrap behavior */
  wrap?: StackWrap;
  /** Whether stack should grow to fill available space */
  grow?: boolean;
  /** Whether stack should take full width */
  fullWidth?: boolean;
  /** Whether stack should take full height */
  fullHeight?: boolean;
  /** Additional class name */
  className?: string;
  /** Test id for testing */
  'data-testid'?: string;
}

const StyledStack = styled.div<{
  $direction: StackDirection;
  $align: StackAlignment;
  $justify: StackJustify;
  $gap: number;
  $wrap: StackWrap;
  $grow: boolean;
  $fullWidth: boolean;
  $fullHeight: boolean;
}>`
  display: flex;
  flex-direction: ${({ $direction }) =>
    $direction === 'vertical' ? 'column' : 'row'};
  align-items: ${({ $align }) => alignmentMap[$align]};
  justify-content: ${({ $justify }) => justifyMap[$justify]};
  gap: ${({ $gap }) => $gap}px;
  flex-wrap: ${({ $wrap }) => $wrap};
  flex-grow: ${({ $grow }) => ($grow ? 1 : 0)};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  height: ${({ $fullHeight }) => ($fullHeight ? '100%' : 'auto')};
`;

/**
 * Stack component based on Libertify Design System
 *
 * A flexible layout component for arranging items horizontally or vertically.
 *
 * @example
 * ```tsx
 * import { Stack } from '@libertify/ui';
 *
 * // Horizontal stack with gap
 * <Stack direction="horizontal" gap="md">
 *   <Button>One</Button>
 *   <Button>Two</Button>
 * </Stack>
 *
 * // Vertical stack centered
 * <Stack direction="vertical" align="center" gap="lg">
 *   <Text>Title</Text>
 *   <Text>Subtitle</Text>
 * </Stack>
 *
 * // Full width with space between
 * <Stack fullWidth justify="between" align="center">
 *   <Logo />
 *   <Navigation />
 * </Stack>
 * ```
 */
export const Stack = ({
  children,
  direction = 'horizontal',
  align = 'stretch',
  justify = 'start',
  gap = 'md',
  wrap = 'nowrap',
  grow = false,
  fullWidth = false,
  fullHeight = false,
  className,
  'data-testid': testId,
}: StackProps) => {
  const gapValue = typeof gap === 'number' ? gap : spacingMap[gap];

  return (
    <StyledStack
      $direction={direction}
      $align={align}
      $justify={justify}
      $gap={gapValue}
      $wrap={wrap}
      $grow={grow}
      $fullWidth={fullWidth}
      $fullHeight={fullHeight}
      className={className}
      data-testid={testId}
    >
      {children}
    </StyledStack>
  );
};

export default Stack;
